import React, { forwardRef, useEffect, useRef, useState } from 'react';
import {
	Box,
	ButtonBase,
	CircularProgress,
	Grid,
	IconButton,
	Skeleton,
	TextField,
	Typography,
} from '@mui/material';
import SideNavigationBar from '../../components/SideNavigationBar';
import { helperText, useMainCustomStyle } from '../../assets/theme/Theme';
import {
	ChatBotBubbleIcon,
	ChatBotIcon,
	InputImage,
	NewChatIcon,
	RecentChatIcon,
	SendChatIcon,
	SendInputIcon,
	SendLinkIcon,
} from '../../assets/icons/Icons';
import {
	fetchAllRecentChatMessages,
	fetchRecentChatMessage,
	sendChatMessage,
} from '../../services/MedicalChatService';
import { toast } from 'react-toastify';
import ChatLoader from '../../assets/images/ChatLoader.gif';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import { getUserId, getUserType } from '../../Utils/AuthUtil';
import UserLimitationPopup from '../../components/UserLimitationPopup';
import { USER_TYPES } from '../../Utils/Enums';

const INITIAL_ERROR_STATE = {
	status: false,
	msg: '',
};

function ChatBubble({ data }) {
	function getChatBubbleStyle() {
		if (data.position === 'left') {
			return {
				borderTopRightRadius: '10px',
				borderBottomLeftRadius: '10px',
				borderBottomRightRadius: '10px',
			};
		} else {
			return {
				borderTopLeftRadius: '10px',
				borderBottomLeftRadius: '10px',
				borderBottomRightRadius: '10px',
			};
		}
	}

	function isMessageIsTooLong() {
		const textLength = data.text.length;
		if (textLength > 50) {
			return true;
		} else {
			return false;
		}
	}

	return (
		<>
			{data.position === 'left' && <ChatBotBubbleIcon />}
			<Grid
				item
				paddingX={'10px'}
				marginTop={'10px'}
				marginLeft={'5px'}
				justifyContent={'center'}
				alignItems={'center'}
				zeroMinWidth
				backgroundColor={
					data.position === 'left' ? '#F2F4F5' : '#28696D'
				}
				paddingY={
					!data.isReceived && data.position === 'left'
						? '10px'
						: '0px'
				}
				width={isMessageIsTooLong() ? '70%' : null}
				style={getChatBubbleStyle()}>
				<Typography
					component={'span'}
					variant="body2"
					style={{ overflowWrap: 'break-word' }}
					color={data.position === 'left' ? '#000000' : '#FFFFFF'}>
					<ReactMarkdown>{data.text}</ReactMarkdown>
					{!data.isReceived && data.position === 'left' && (
						<img src={ChatLoader} alt="loading..." />
					)}
				</Typography>
			</Grid>
		</>
	);
}

function ChatRow({ chat }) {
	return (
		<Grid
			container
			justifyContent={
				chat.position === 'right' ? 'flex-end' : 'flex-start'
			}
			padding={'10px'}>
			<ChatBubble data={chat} />
		</Grid>
	);
}

const ChatBodyWindow = forwardRef(function ChatBodyWindow(
	{ isChatView, chatData, loading },
	ref,
) {
	if (isChatView && !loading) {
		return (
			<Box
				ref={ref}
				sx={{
					overflowY: 'auto',
					height: {
						md: '54vh',
						xs: '50vh',
					},
					width: '100%',
					'&::-webkit-scrollbar': {
						display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
					},
					'&-ms-overflow-style:': {
						display: 'none', // Hide the scrollbar for IE
					},
				}}>
				<Grid padding={'10px'}>
					{chatData.map((item, index) => {
						return (
							<ChatRow
								key={index}
								chat={{
									position:
										item.type === 'RESPONSE'
											? 'left'
											: 'right',
									type: 'text',
									text: item.message,
									isReceived: item.isReceived,
								}}
							/>
						);
					})}
				</Grid>
			</Box>
		);
	} else if (isChatView && loading) {
		return (
			<Grid
				container
				height="55vh"
				backgroundColor={'#FFFFFF'}
				justifyContent={'center'}
				alignItems={'center'}>
				<CircularProgress sx={{ color: '#28696D' }} />
			</Grid>
		);
	} else {
		return (
			<Grid
				container
				height="40vh"
				flexDirection={'column'}
				justifyContent={'center'}
				textAlign={'center'}
				alignItems={'center'}>
				<Typography
					fontSize={'24px'}
					color={'#28696D'}
					fontWeight={'400'}>
					Welcome to Senz Care Medical Assistant!
				</Typography>
				<Typography
					fontSize={'24px'}
					color={'#9CA3AF'}
					fontWeight={'400'}>
					What can I help you?
				</Typography>
			</Grid>
		);
	}
});

function RecentChatWindow({
	recentChatData,
	onClickRecentChat,
	selectedChatId,
}) {
	if (recentChatData && recentChatData.length > 0) {
		return (
			<Grid
				container
				flexDirection={'row'}
				padding={'10px'}
				rowGap={1}
				justifyContent={'center'}>
				{recentChatData.map((value, index) => {
					return (
						<ButtonBase
							key={index}
							sx={{
								width: '100%',
								paddingY: '5px',
							}}
							disableRipple
							onClick={() => onClickRecentChat(value.id)}>
							<Grid
								container
								width={'100%'}
								padding={'10px'}
								textAlign={'left'}
								bgcolor={
									selectedChatId === value.id
										? '#87B6B31F'
										: '#FFFFFF'
								}
								flexDirection={'column'}
								border={'1px #E5E7EB solid'}
								borderRadius={'5px'}>
								<Grid
									container
									flexDirection={'row'}
									paddingBottom={'5px'}
									justifyContent={'space-between'}
									alignItems={'center'}>
									<RecentChatIcon />
									{value.id ? (
										<Typography
											bgcolor={'#87B6B31F'}
											paddingX={'10px'}
											fontSize={'10px'}
											borderRadius={'10px'}
											color="#28696D">
											{`Ref:${value.id.length <= 8 ? value.id : value.id.substr(0, 8) + '...'}`}
										</Typography>
									) : (
										<Skeleton width={'40%'} />
									)}
								</Grid>
								{value.chatTitle ? (
									<Typography
										fontWeight={'700'}
										fontSize={'14px'}
										marginY={'5px'}
										color={'#787486'}>
										{`${value.chatTitle.length <= 18 ? value.chatTitle : value.chatTitle.substr(0, 18) + '...'}`}
									</Typography>
								) : (
									<Skeleton width={'100%'} height={20} />
								)}
								{value.message ? (
									<Typography
										sx={{
											whiteSpace: 'normal',
											wordBreak: 'break-word',
										}}
										fontSize={'12px'}
										color={'#98A2B3'}>
										{value.message}
									</Typography>
								) : (
									<Skeleton width={'100%'} height={40} />
								)}
							</Grid>
						</ButtonBase>
					);
				})}
			</Grid>
		);
	} else {
		return (
			<Grid container paddingLeft={'10px'} flexDirection={'column'}>
				<Typography
					padding={'10px'}
					color={'#303437'}
					fontSize={'14px'}>
					Recent Chats
				</Typography>
				<Typography
					fontSize={'12px'}
					padding={'20px'}
					color={'#98A2B3'}
					textAlign={'center'}>
					No recent chats
				</Typography>
			</Grid>
		);
	}
}

function MedicalAssistant() {
	const mainDivStyle = useMainCustomStyle();
	const messagesEndRef = useRef(null);
	const [chatId, setChatId] = useState(null);
	const [message, setMessage] = useState('');
	const [isChatWindow, setIsChatWindow] = useState(false);
	const [selectedChatData, setSelectedChatData] = useState([]);
	const [recentChatData, setRecentChatData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedFile, setSelectedFile] = useState([]);
	const [previewUrl, setPreviewUrl] = useState('');
	const [isSendBtnDisabled, setIsSendBtnDisabled] = useState(false);
	const [openLimitationModal, setOpenLimitationModal] = useState(false);
	const [isThisNewChat, setIsThisNewChat] = useState(true);
	const [remainingGeneration, setRemainingGeneration] = useState({
		count: 0,
		progress: 0,
		text: '',
	});
	const isDemoUser = getUserType() === USER_TYPES[2].key;
	const [fileError, setFileError] = useState(INITIAL_ERROR_STATE);

	useEffect(() => {
		fetchRecentChatList();
	}, []);

	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.style.scrollBehavior = 'auto';

			messagesEndRef.current.scrollTop =
				messagesEndRef.current.scrollHeight;

			setTimeout(() => {
				messagesEndRef.current.style.scrollBehavior = 'smooth';
			}, 100);
		}
	}, [loading, selectedChatData]);

	async function fetchRecentChatList(isLoading = true) {
		if (isLoading) {
			const skeletonLoaderArr = Array(6).fill({
				id: null,
				chatTitle: null,
				message: null,
			});
			setRecentChatData(skeletonLoaderArr);
		}

		const body = {
			auth: {
				user_id: getUserId(),
				is_demo: isDemoUser,
			},
		};

		const res = await fetchAllRecentChatMessages(body);

		if (!res.error) {
			if (Object.entries(res.chats).length > 0) {
				const filteredChatData = Object.keys(res.chats).map(item => {
					const chat = res.chats[item];

					return {
						id: chat.chat_id,
						chatTitle: chat.title.replace(/"/g, ''),
						message: chat.description,
					};
				});

				setRecentChatData(filteredChatData.reverse());
			} else {
				setRecentChatData([]);
			}
		} else {
			if (res.data && res.data.limit_reached) {
				setOpenLimitationModal(true);
				setRemainingGeneration({
					count: 0,
					progress: 100 - (0 / 5) * 100,
					text: `You've reached chat session limit and have 0 remaining.`,
				});
			} else {
				toast.error(res.message);
			}
		}
	}

	function onPressNewChat() {
		setSelectedChatData([
			{
				message: 'How can i help you...',
				type: 'RESPONSE',
				isReceived: true,
			},
		]);
		setIsChatWindow(true);
		setIsThisNewChat(true);
	}

	async function onSendChat() {
		setIsSendBtnDisabled(true);
		setMessage('');
		if (!isChatWindow) {
			setIsChatWindow(true);
		}

		const newMessages = [
			{
				message: message,
				type: 'QUESTION',
			},
			{
				message: '',
				type: 'RESPONSE',
				isReceived: false,
			},
		];

		const clone = [...selectedChatData, ...newMessages];
		setSelectedChatData(clone);

		const body = {
			chat_id: chatId ? chatId : uuidv4(),
			message: message,
			auth: {
				user_id: getUserId(),
				is_demo: isDemoUser,
			},
		};

		const responseData = await sendChatMessage(body);

		if (!responseData.error) {
			clone.splice(-1);
			clone.push({
				message: responseData.response,
				type: 'RESPONSE',
				isReceived: true,
			});

			setSelectedChatData([...clone]);
			setIsSendBtnDisabled(false);

			if (isThisNewChat) {
				fetchRecentChatList(false);
				setIsThisNewChat(false);
			}
		} else {
			if (responseData.data && responseData.data.limit_reached) {
				setOpenLimitationModal(true);
				setRemainingGeneration({
					count: 0,
					progress: 100 - (0 / 5) * 100,
					text: `You've reached chat limit and have 0 remaining.`,
				});
			} else {
				clone.splice(-1);
				clone.push({
					message: responseData.message,
					type: 'RESPONSE',
					isReceived: true,
				});

				setSelectedChatData([...clone]);
				setIsSendBtnDisabled(false);
			}
			setIsSendBtnDisabled(false);
		}
	}

	function onSelectAttachments(event) {
		const file = event.target.files[0];
		const ext = file.name.split('.').pop().toLowerCase();
		const isImage = file.type.startsWith('image');
		if (!isImage && !['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setFileError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (!isImage && file && file.size === 0) {
			setFileError({
				status: true,
				msg: `${file.name ? file.name : 'The selected'} file is empty.`,
			});
			return;
		}

		if (!isImage && file.size && file.size > 5242880) {
			setFileError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setSelectedFile(prevState => [...prevState, file]);
		setFileError(INITIAL_ERROR_STATE);
	}

	async function onViewRecentChat(id) {
		setIsChatWindow(true);
		setLoading(true);
		setIsThisNewChat(false);

		const body = {
			chat_id: id,
			auth: {
				user_id: getUserId(),
				is_demo: isDemoUser,
			},
		};

		const data = await fetchRecentChatMessage(body);

		if (!data.error) {
			const chats = [];
			data.messages.map(item => {
				chats.push({
					message: item.question,
					type: 'QUESTION',
				});

				chats.push({
					message: item.response,
					type: 'RESPONSE',
					isReceived: true,
				});
			});

			setSelectedChatData([...chats]);
			setChatId(id);
			setLoading(false);
		} else {
			toast.error(data.message);
		}
	}

	function onRenderSelectedFile(file, index) {
		const previewHeight = 100;

		const commonBoxStyle = {
			marginY: '5px',
			boxShadow: 2,
			backgroundColor: '#EEF1F7',
			borderRadius: '10px',
			padding: '10px',
			height: `${previewHeight}px`,
			overflow: 'hidden',
			display: 'flex',
		};

		if (file.type.startsWith('image')) {
			const preview = URL.createObjectURL(file);

			return (
				<Box
					key={index}
					sx={{
						...commonBoxStyle,
						justifyContent: 'center',
						alignItems: 'center',
					}}>
					<img
						src={preview}
						alt="Preview"
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'contain',
						}}
					/>
				</Box>
			);
		}

		return (
			<Box
				key={index}
				sx={{
					...commonBoxStyle,
					flexDirection: 'column',
					justifyContent: 'center',
				}}>
				<Typography
					sx={{
						fontSize: '14px',
						color: '#292D32',
						wordBreak: 'break-word',
						textAlign: 'left',
					}}>
					{file.name}
				</Typography>
			</Box>
		);
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid
					container
					paddingLeft={{ xs: '0px', md: '20px', sm: '20px' }}
					paddingRight={'20px'}>
					<Grid paddingLeft="30px" paddingBottom={'20px'}>
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							Get Medical Advice
						</Typography>
					</Grid>
					<Grid
						container
						marginLeft={'10px'}
						columnSpacing={2}
						rowSpacing={{ xs: 2 }}>
						<Grid item xs={12} md={3}>
							<Grid
								sx={{
									backgroundColor: '#FFFFFF',
									height: '78vh',
									overflowY: 'auto',
									'&::-webkit-scrollbar': {
										display: 'none',
									},
									scrollbarWidth: 'none',
									borderRadius: '5px',
								}}>
								<RecentChatWindow
									recentChatData={recentChatData}
									onClickRecentChat={onViewRecentChat}
									selectedChatId={chatId}
								/>
							</Grid>
						</Grid>
						<Grid item xs={12} md={9}>
							<Grid
								bgcolor={'#FFFFFF'}
								borderRadius={'5px'}
								sx={{ height: '78vh' }}>
								<Grid
									container
									justifyContent={'space-between'}
									alignItems={'center'}
									borderBottom={'1px solid #E5E7EB'}
									padding={'10px'}>
									<Grid
										container
										width={'30%'}
										alignItems={'center'}>
										<ChatBotIcon />
										<Typography
											fontWeight={'700'}
											color="#000000"
											fontSize={'14px'}
											paddingLeft="15px">
											Medical Assistant
										</Typography>
									</Grid>
									<IconButton
										disableRipple
										onClick={onPressNewChat}
										sx={{
											border: '1px solid #28696D',
											paddingX: '5px',
											color: '#28696D',
											textTransform: 'none',
											borderRadius: '10px',
											height: '40px',
											alignItems: 'center',
										}}>
										<NewChatIcon />
										<Typography
											fontWeight={'600'}
											fontSize={'14px'}
											color="#28696D"
											paddingLeft="8px">
											New Chat
										</Typography>
									</IconButton>
								</Grid>
								<Grid container>
									<ChatBodyWindow
										isChatView={isChatWindow}
										chatData={selectedChatData}
										ref={messagesEndRef}
										loading={loading}
									/>
									<Grid
										container
										marginX={'20px'}
										paddingX={'10px'}
										paddingBottom={'10px'}
										sx={{
											borderTopLeftRadius: '15px',
											borderTopRightRadius: '15px',
										}}
										bgcolor={
											selectedFile.length > 0 && '#F2F4F5'
										}>
										<Grid container columnGap={1}>
											{selectedFile.map((file, index) =>
												onRenderSelectedFile(
													file,
													index,
												),
											)}
										</Grid>
										{fileError.status && (
											<Typography
												sx={[
													helperText,
													{ marginTop: '10px' },
												]}>
												{fileError.msg}
											</Typography>
										)}
									</Grid>
									<Grid
										container
										alignItems={'center'}
										marginX={'20px'}
										marginBottom={'5px'}
										paddingX={'10px'}
										paddingY={'5px'}
										borderRadius={'20px'}
										sx={
											selectedFile && {
												borderTopLeftRadius: '0px',
												borderTopRightRadius: '0px',
											}
										}
										bgcolor={' #F2F4F5'}>
										<TextField
											type="text"
											variant="standard"
											placeholder="Type your message here..."
											sx={{
												width: {
													md: '93%',
													xs: '82%',
												},
												'& fieldset': {
													border: 'none',
												},
											}}
											InputProps={{
												startAdornment: <InputImage />,
												disableUnderline: true,
											}}
											inputProps={{
												style: {
													color: '#000000',
													paddingLeft: '5px',
													fontSize: '14px',
												},
											}}
											value={message}
											onChange={e =>
												setMessage(e.target.value)
											}
											onKeyDown={e => {
												e.key === 'Enter' &&
													onSendChat();
											}}
										/>
										<Grid justifyContent={'flex-end'}>
											<IconButton component="label">
												<SendLinkIcon />
												<input
													hidden
													type="file"
													onChange={
														onSelectAttachments
													}
												/>
											</IconButton>
											<IconButton
												onClick={onSendChat}
												disabled={isSendBtnDisabled}>
												<SendChatIcon />
											</IconButton>
										</Grid>
									</Grid>
									<Grid
										container
										alignItems={'center'}
										marginX={'5px'}>
										<Typography
											fontSize={'12px'}
											color={'#98A2B3'}
											align={'center'}>
											This AI medical bot provides general
											elder care related information and
											guidance. Always consult a
											healthcare professional for medical
											decisions. In emergencies, seek
											immediate medical attention
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</div>
			<UserLimitationPopup
				isOpen={openLimitationModal}
				onClose={() => setOpenLimitationModal(false)}
				text={remainingGeneration.text}
				progress={remainingGeneration.progress}
			/>
		</>
	);
}

export default MedicalAssistant;
